<template>
  <div>
    <b-card no-body class="overflow-hidden">
      <b-row no-gutters class="p-2">
        <b-col
          cols="12"
          class="mb-2 pb-2 border-bottom d-flex align-items-center justify-content-between w-100"
          v-if="showHeader"
        >
          <small
            v-b-tooltip.hover
            :title="pathOr('', ['caption'], file)"
            class="text-truncate font-weight-bold"
            v-text="pathOr('', ['caption'], file)"
          />
          <span
            v-if="isAdmin(permissions)"
            class="text-success cursor_pointer"
            v-b-tooltip.hover
            :title="$lang.edit_title_type_attachment"
            @click="showEditLabelModal(file)"
          >
            <mdicon name="file-document-edit-outline" size="20" />
          </span>
        </b-col>
        <b-col cols="auto" md="auto" class="bg-light rounded text-center px-0">
          <b-form-checkbox
            v-if="showCheckbox"
            v-model="file.default"
            @change.native="changeDefault(file.id, $event)"
            class="position-absolute"
          />
          <b-card-img
            :src="thumbnail"
            :onerror="`this.src='${$config.images.shared.noImage}'`"
            class="square_image"
            :class="
              pathOr(null, ['attachment_type', 'id'], file) === 10
                ? 'object__fit--cover'
                : 'object__fit--contain'
            "
            @click="isImage(file) && showPreview(file)"
          />
        </b-col>
        <b-col md="" class="px-0 text-right col">
          <div class="text-date dir_ltr" v-if="file.created_at">
            {{ file.created_at.jalali }}
          </div>
          <div class="text-name d-none">{{ spliceName(name) }}</div>
          <div class="text-size dir_ltr">
            {{ bytesToSize(file.size) }}
            <span v-if="file.ext">| {{ file.ext }}</span>
          </div>
          <div class="text-size text-left">
            {{ pathOr("", ["attachment_type", "caption"], file) }}
          </div>
        </b-col>
      </b-row>
      <b-row v-if="file.buttons" class="m-0 py-1 border-top">
        <b-col
          cols="3"
          class="d-flex align-items-center justify-content-center p-0"
          v-for="(btn, key) in availableButtons(file.buttons)"
          :key="key"
        >
          <span
            v-if="!btn.href"
            @click="btn.click(file)"
            class="width_32 height_32 bg-light rounded-circle d-flex align-items-center justify-content-center cursor_pointer text-dark"
          >
            <i :class="btn.iconFont" />
          </span>
          <span
            v-if="btn.href"
            class="width_32 height_32 bg-light rounded-circle d-flex align-items-center justify-content-center cursor_pointer text-dark"
            @click="download(file.download_code)"
          >
            <mdicon v-if="downloadLoading" name="loading" class="spinner" />
            <mdicon v-else name="download-circle-outline" />
          </span>
        </b-col>
      </b-row>
      <b-row
        v-else
        align-v="center"
        align-h="center"
        class="m-0 p-0 w-100 border-top"
      >
        <b-col />
        <b-col cols="auto" sm="auto" md="auto" class="p-2">
          <span
            class="width_32 height_32 bg-light rounded-circle d-flex align-items-center justify-content-center cursor_pointer text-dark"
            v-if="showRemove"
            @click="removeFile(file)"
          >
            <i class="fa fa-trash" />
          </span>
        </b-col>
      </b-row>
    </b-card>
    <b-modal
      no-close-on-backdrop
      hide-header
      hide-footer
      v-model="editTitleTypeAttachmentModal"
    >
      <b-card>
        <div slot="header">
          <b v-text="$lang.edit_title_type_attachment" />
        </div>
        <LoadingSpinner
          :loading="editTitleTypeAttachmentModalLoading"
          :text="$lang.sending_data"
        />
        <b-row v-if="!editTitleTypeAttachmentModalLoading">
          <b-col>
            <b-form-group
              v-if="hasEditTypeAttachment"
              :label="$lang.attachment_type"
              label-for="attachment_type"
              :label-cols="this.$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.attachment_type"
                :rules="$v.attachment_type_selected.$params"
              />
              <selectFind
                :options="attachment_type_options"
                v-model="$v.attachment_type_selected.$model"
                label="caption"
                id="id"
                :isValid="
                  $v.attachment_type_selected.$dirty
                    ? !$v.attachment_type_selected.$error
                    : null
                "
                @input="changeCaption"
              />
              <b-form-invalid-feedback
                :style="{
                  display: $v.attachment_type_selected.$error
                    ? 'block'
                    : 'none',
                }"
                id="attachment_type_selected_feedback"
              >
                {{ $lang.error_required | replace($lang.attachment_type) }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
              :label="$lang.caption"
              label-for="caption"
              :label-cols="$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.caption"
                :rules="$v.caption.$params"
              />
              <b-form-input
                id="caption"
                type="text"
                autocomplete="off"
                v-model="$v.caption.$model"
                :state="$v.caption.$dirty ? !$v.caption.$error : null"
              >
              </b-form-input>
              <b-form-invalid-feedback id="caption-feedback"
                >{{ $lang.error_required | replace($lang.caption) }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="12" class="text-right">
            <b-button
              class="mr-2"
              variant="outline-danger"
              @click="closeEditTitleTypeAttachmentModal"
            >
              <mdicon name="close" size="20" />
              {{ $lang.close }}
            </b-button>
            <b-button variant="success" @click="updateTitleTypeAttachment">
              <mdicon name="check" size="20" />
              {{ $lang.submitButton }}
            </b-button>
          </b-col>
        </b-row>
      </b-card>
    </b-modal>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/base/LoadingSpinner";
import { required } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { generateApiUrl, renderEumOptions, isAdmin } from "@/shared/utils";
import {
  getAttachmentDownloadFile,
  getAttachmentDownloadSrc,
} from "@/services";

export default {
  components: { LoadingSpinner },
  props: {
    showCheckbox: {
      type: Boolean,
      default: false,
    },
    file: {
      type: [Object, File],
    },
    showDownload: {
      type: Boolean,
      default: true,
    },
    showRemove: {
      type: Boolean,
      default: true,
    },
    name: {
      type: String,
      default: "",
    },
    permissions: {
      type: Array,
      default: () => [],
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    keyService: {
      type: String,
      default: null,
    },
    keyValue: {
      type: [String, Number],
      default: null,
    },
    hasEditTypeAttachment: {
      type: Boolean,
      default: false,
    },
    attachment_type_name: {
      type: String,
      default: "attachment_type_person",
    },
  },
  validations: {
    attachment_type_selected: {
      required,
    },
    caption: {},
  },
  data() {
    return {
      defaultButtons: [
        {
          id: "1",
          name: "download", // دانلود
          iconFont: "fa fa-download",
          AllowPermissions: ["admin", "edit", "view"],
        },

        {
          id: "3",
          name: "delete", //حذف
          iconFont: "fa fa-trash",
          AllowPermissions: ["admin"],
          click: this.removeFile,
        },
        {
          id: "4",
          name: "crop", //برش
          iconFont: "fa fa-crop",
          AllowPermissions: ["view", "admin", "edit"],
          click: this.crop,
        },
        {
          id: "5",
          name: "edit", //نمایش
          iconFont: "fa fa-eye",
          AllowPermissions: ["view", "admin", "edit"],
          click: this.showPreview,
        },
      ],
      valueProgress: 0,
      editTitleTypeAttachmentModalLoading: false,
      attachment_type_selected: null,
      caption: null,
      editTitleTypeAttachmentModal: false,
      currentEditFile: null,
      isGetAttachmentDownloadSrcLoading: false,
      thumbnail: "",
      downloadLoading: false,
    };
  },
  computed: {
    attachment_type_options() {
      return renderEumOptions(this.attachment_type_name).map((item) => {
        return {
          id: pathOr(null, ["value"], item),
          caption: pathOr(null, ["text"], item),
        };
      });
    },
  },
  methods: {
    pathOr,
    renderEumOptions,
    isAdmin,
    availableButtons(item) {
      let generateButtons = [];
      if (typeof this.file.buttons === "object")
        generateButtons = this.file.buttons;
      else generateButtons = this.file.buttons(item);

      let buttons = generateButtons
        .filter((button) => {
          let permission = this.defaultButtons.find(
            (x) => x.name == button.name
          );

          // compare button permission and user permissions
          let ok = Object.values(this.permissions).filter((x) =>
            permission.AllowPermissions.includes(x)
          );
          // compare keys button has and defult button dosent have
          return ok.length > 0;
        })
        .map((but) => {
          let temp = Object.assign({}, but); // clone
          let permission = this.defaultButtons.find((x) => x.name == but.name);
          if (permission.name == "enable") {
            temp.iconFont =
              item.is_enable == 1
                ? "fa fa-unlock text-warning fa-lg"
                : "fa fa-lock text-warning fa-lg";
            temp.text =
              item.is_enable == 1
                ? this.$lang.search.enabled_tooltip
                : this.$lang.search.disabled_tooltip;
          }

          // set show flag
          let show = true;
          // check when file is not image type remove crop button
          if (permission.name === "crop") {
            show = this.isImage(this.file);
          }

          return {
            ...permission,
            ...temp,
            show,
          };
        });

      return buttons.filter(({ show }) => show);
    },
    spliceName(name) {
      if (name) return name.split(".")[0];
    },
    bytesToSize(bytes, seperator = "") {
      if (!bytes) return "0";
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      if (bytes == 0) return "n/a";
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
      if (i === 0) return `${bytes}${seperator}${sizes[i]}`;
      return `${(bytes / 1024 ** i).toFixed(1)}${seperator}${sizes[i]}`;
    },
    changeDefault(id, event) {
      let payload = {
        checked: event.target.checked,
        id: id,
      };
      this.$emit("checkbox", payload);
    },
    checkPreview(file) {
      if (file.url && file.type && file.type.match("image")) return true;
      if (file.url && file.type && file.type.match("video")) return false;
      return false;
    },
    videoPreview(file) {
      if (file.url && file.type && file.type.match("video")) {
        return true;
      } else {
        return false;
      }
    },
    removeFile(id) {
      this.$emit("removeFile", id);
    },
    isImage(file) {
      const fileType = pathOr(null, ["type"], file);
      return fileType.match("image");
    },
    showPreview(file) {
      this.$emit("showPrivew", file);
    },
    crop(file) {
      this.$emit("crop", file);
    },
    getImg(file) {
      const fileType = file.type;
      const fileName = file.name.split(".")[1]; // get file by suffix name
      return fileType.match("image")
        ? file.thumb
        : `/img/shared/file-icon/${fileName}.svg`;
    },
    async download(downloadCode) {
      if (!downloadCode) return;
      try {
        this.downloadLoading = true;
        await getAttachmentDownloadFile(downloadCode);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.downloadLoading = false;
      }
    },
    closeEditTitleTypeAttachmentModal() {
      this.editTitleTypeAttachmentModal = false;
    },
    showEditLabelModal(file) {
      this.currentEditFile = file;

      this.attachment_type_selected = pathOr(null, ["attachment_type"], file);
      this.caption = pathOr(null, ["caption"], file);
      this.editTitleTypeAttachmentModal = true;
    },
    loadFiles() {
      this.$emit("loadFiles");
    },
    async updateTitleTypeAttachment() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      try {
        this.editTitleTypeAttachmentModalLoading = true;
        const code = pathOr(null, ["code"], this.currentEditFile);
        const url = generateApiUrl(
          ["resources", "attachment_change"],
          [this.keyService, this.keyValue, code]
        );
        const payload = {
          attachment_type_id: pathOr(
            null,
            ["id"],
            this.attachment_type_selected
          ),
          caption: this.caption,
        };
        const response = await this.$httpService.patch(url, payload);
        this.$notify.success(response.data.message);
        this.closeEditTitleTypeAttachmentModal();
        this.loadFiles();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.editTitleTypeAttachmentModalLoading = false;
      }
    },
    changeCaption(item) {
      this.caption = pathOr(null, ["caption"], item);
    },
    async handleGetAttachmentDownloadSrc(code) {
      if (this.isGetAttachmentDownloadSrcLoading || !code) return;

      try {
        this.isGetAttachmentDownloadSrcLoading = true;
        const { src } = await getAttachmentDownloadSrc(code);
        this.thumbnail = src;
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.isGetAttachmentDownloadSrcLoading = false;
      }
    },
  },
  watch: {
    file: {
      immediate: true,
      handler({ download_code }) {
        this.handleGetAttachmentDownloadSrc(download_code);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.card-main-img {
  width: 100px;
  margin: auto;
}

.bg-lightgray {
  background-color: #f0f3f5;
}

.text-name {
  font-size: 12px;
  max-width: 12rem;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  direction: ltr;
  text-overflow: ellipsis;
}

.text-date {
  font-size: 11px;
  padding: 0 4px;
  color: #999;
}

.text-size {
  font-size: 11px;
  padding: 0 4px;
  color: #888;
}

.btn-style {
  border-top: 1px solid #ebebeb;
  position: absolute;
  bottom: 0;
  width: 99%;
  padding-top: 5px;
  text-align: left;
}

.icon {
  color: #23282c;

  &:hover {
    color: #584d4d;
  }
}

.text-header-card {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.position-right-top {
  position: absolute;
  top: 35px;
  right: 10px;
  z-index: 999;
  color: #28a745;
}
</style>
